/**
 * Wire Electron IPC sync events → hotelmis custom events the UI already listens for.
 *
 * BLINK FIX: hotelmis:data:mutated is only fired when something actually changed
 * (synced > 0, or a snapshot was explicitly pushed with changed=true).
 * Previously it fired on EVERY sync cycle completion — even idle ones — causing
 * all 30+ useOfflineInvalidation hooks to simultaneously reload their data.
 *
 * OFFLINE-ID RECONCILIATION (2026-06-04):
 * The backend now returns SyncCommandResultDto.clientEntityId in every batch
 * result.  When clientEntityId starts with "offline-", the server resolved a
 * client-side temp-id to a real UUID.  We do three things here:
 *
 *   1. Write offlineId → realId into syncStore.offlineResolutions so every
 *      subsequent desktopGet() call for that offline-id is transparently
 *      redirected to the real UUID (see desktopApiFetch.js).
 *
 *   2. Fire "hotelmis:offline:resolved" so App.jsx can update selectedId when
 *      the user is currently on a detail page for the reconciled entity.
 *
 *   3. Call window.pms.reconcileOfflineId() — IPC to the Electron main process
 *      so it can tombstone the ghost offline SQLite row and rewrite any
 *      subsequent queued WAL events that still reference the offline-id.
 */
import { installDesktopAuthWatcher } from './desktopAuthSync.js';
import { useSyncStore } from '../stores/syncStore.js';

export function initDesktopBridge() {
  if (!window.pms) return;
  installDesktopAuthWatcher();

  let lastOnline = null;

  window.pms.onSyncStatus((status) => {
    const online = status.connection === 'ONLINE';

    // ── Online / offline indicator ────────────────────────────────────────
    if (lastOnline !== online) {
      lastOnline = online;
      window.dispatchEvent(new Event(online ? 'hotelmis:backend:online' : 'hotelmis:backend:offline'));
    }

    // ── Sync progress indicators (for the spinner/badge in FrontDeskLayout) ──
    // Gate on pending > 0: idle sync cycles (nothing queued to flush) must NOT
    // fire sync:start — that would make the TopNav flicker "Syncing" every
    // 60 s even when the app is fully online with no pending mutations.
    if (status.syncing && (status.pending || 0) > 0) {
      window.dispatchEvent(new CustomEvent('hotelmis:generic:sync:start', {
        detail: { total: status.pending || 1 },
      }));
    }

    // ── Data-changed notification — ONLY when something actually synced ────
    if (status.phase === 'done') {
      const syncDetail = { synced: status.synced ?? 0, failed: status.failed ?? 0 };

      // 'hotelmis:generic:sync:done' — existing listeners (usePendingSync, etc.)
      window.dispatchEvent(new CustomEvent('hotelmis:generic:sync:done', { detail: syncDetail }));

      // 'hotelmis:sync:done' — the name useOfflineInvalidation's onDone handler
      // actually listens for.  Previously this was never fired (only the
      // 'generic' variant was), so onDone callbacks were dead code and views
      // never auto-refreshed when the SyncWorker completed a cycle.
      // Only fire when something actually changed to avoid blink on idle cycles.
      if ((status.synced ?? 0) > 0) {
        window.dispatchEvent(new CustomEvent('hotelmis:sync:done', { detail: syncDetail }));
        // Broad invalidation so any view with a useOfflineInvalidation hook
        // reloads — path/prefix matching in the bump handler is satisfied by
        // the targeted post-sync fire in desktopApiFetch; this covers any view
        // that wasn't the initiator of the mutation (e.g. Dashboard refreshing
        // after a check-in triggered from FrontDesk).
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'sync', synced: status.synced },
        }));
      }
    }

    if (status.conflicts > 0) {
      window.dispatchEvent(new CustomEvent('hotelmis:desktop:conflicts', {
        detail: { count: status.conflicts },
      }));
    }
  });

  // ── Snapshot updates ──────────────────────────────────────────────────────
  window.pms.onSnapshotUpdated((payload) => {
    if (payload?.changed) {
      window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
        detail: { source: 'snapshot' },
      }));
      return;
    }

    if (payload?._pending && payload?.entityType) {
      const path = entityTypeToPath(payload.entityType);
      window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
        detail: { source: 'offline', path, invalidatePrefix: path },
      }));

      if (payload.entityType === 'RESERVATION') {
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'rooms', invalidatePrefix: 'rooms' },
        }));
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'dashboard', invalidatePrefix: 'dashboard' },
        }));
        // Reservation status changes affect arrival manifests / occupancy reports
        // and the financial statement (status-gated revenue filters).
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'reports', invalidatePrefix: 'reports' },
        }));
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'financial', invalidatePrefix: 'financial' },
        }));
      }
      if (payload.entityType === 'HOUSEKEEPING_TASK') {
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'rooms', invalidatePrefix: 'rooms' },
        }));
      }
      // Folio charges and payments affect revenue totals shown on the dashboard
      // and the reports revenue tab — reload both when a charge/payment is queued.
      if (payload.entityType === 'FOLIO_CHARGE' || payload.entityType === 'FOLIO_PAYMENT') {
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'dashboard', invalidatePrefix: 'dashboard' },
        }));
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'offline', path: 'reports', invalidatePrefix: 'reports' },
        }));
      }
    }
  });

  // ── Command results ───────────────────────────────────────────────────────
  // FIX (2026-06-04): process clientEntityId for offline-id reconciliation.
  //
  // The backend now returns clientEntityId = the temp offline-id the client
  // originally submitted (e.g. "offline-guest-1780485078502-2329") alongside
  // the real server UUID in entityId.  Without this, the ghost offline record
  // would stay in SQLite forever and components navigated to the offline-id
  // would see null after the next delta pull overwrites it.
  if (window.pms.onCommandResult) {
    window.pms.onCommandResult?.((result) => {
      // ── 1. General data invalidation (existing behaviour) ─────────────────
      if (result?.entityType) {
        const path = entityTypeToPath(result.entityType);
        window.dispatchEvent(new CustomEvent('hotelmis:data:mutated', {
          detail: { source: 'command', path },
        }));
      }

      // ── 2. Offline-id reconciliation ──────────────────────────────────────
      const { clientEntityId, entityId, entityType } = result ?? {};
      if (clientEntityId && clientEntityId.startsWith('offline-') && entityId) {

        // 2a. Write into syncStore so desktopApiFetch.desktopGet can redirect
        //     stale offline-id lookups to the real UUID transparently.
        useSyncStore.getState().addResolution(clientEntityId, entityId, entityType);

        // 2b. Notify components that are currently viewing/holding this offline-id.
        //     App.jsx listens to this event to update selectedId; any other
        //     component that stored the offline-id in local state can do the same.
        window.dispatchEvent(new CustomEvent('hotelmis:offline:resolved', {
          detail: { offlineId: clientEntityId, realId: entityId, entityType },
        }));

        // 2c. Tell the Electron main process to tombstone the ghost SQLite row
        //     and rewrite any remaining WAL queue entries that still reference
        //     the offline-id.  This call is best-effort — the renderer can't
        //     do the SQLite write itself, so we delegate via IPC.
        window.pms.reconcileOfflineId?.({ offlineId: clientEntityId, realId: entityId, entityType })
          .catch?.(() => { /* non-fatal — delta pull will correct SQLite on next cycle */ });
      }
    });
  }
}

/** Map entity type → URL path fragment for targeted invalidation. */
function entityTypeToPath(entityType) {
  switch (entityType) {
    case 'RESERVATION':         return 'reservations';
    case 'GUEST':               return 'guests';
    case 'ROOM':                return 'rooms';
    case 'ROOM_TYPE':           return 'room-types';
    case 'FOLIO_CHARGE':
    case 'FOLIO_PAYMENT':       return 'reservations';
    case 'HOUSEKEEPING_TASK':
    case 'HOUSEKEEPING_STATUS': return 'housekeeping';
    case 'STAFF_MEMBER':        return 'staff';
    case 'RATE_PLAN':           return 'rate-plans';
    case 'PROPERTY':            return 'properties';
    case 'DASHBOARD':           return 'dashboard';
    default:                    return entityType.toLowerCase().replace(/_/g, '-');
  }
}